import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { apiFetch } from '../services/api';
import { useCelular } from '../hooks/useCelular';
import { useNavigate, useSearchParams } from 'react-router-dom';
import aviso, { textoDoErro } from '../lib/aviso';

import ContactList, { type ModoLista } from '../components/ContactList';
import ConversationHeader from '../components/ConversationHeader';
import ConversationWindow from '../components/ConversationWindow';
import MessageInput from '../components/MessageInput';
import DetailsPanel, { type SecaoContato } from '../components/DetailsPanel';
import PageHeader from '../components/PageHeader';
import SummaryModal from '../components/SummaryModal';
import TransferModal from '../components/TransferModal';
import type { AcoesConversa } from '../components/conversaMenu';
import ChatSemConexao from '../components/ChatSemConexao';
import CriarTarefaDialog from '../components/CriarTarefaDialog';
import { TipoTarefaService, type TipoTarefa } from '../services/TarefaService';
import { AtalhoService, type Atalho } from '../services/AtalhoService';
import { resolverAtalho } from '../lib/atalhos';
import { instanteDoLembrete, instanteDoSilencio, textoDaVolta, type PrazoLembrete, type PrazoSilencio } from '../lib/prazosChat';
import { nomeEtapa } from '../data/etapas';
import { getUserPermissions } from '../config/permissions';
/* O CSS antigo segue importado de propósito: 31 classes dele são a última
   definição de estilos que outras telas consomem. Ver o topo de ChatScreen.css. */
import './ChatPage.css';
import './ChatScreen.css';
import type { Contact, Message, InternalUser } from '../data/mockData';
import type { AtribuicaoAnuncio } from '../services/TrafegoPagoService';
import hubService from '../hub/HubConnectionService';
import { ChatService } from '../services/ChatService';
import { ContactService } from '../services/ContactService';
import { RegrasIAService } from '../services/RegrasIAService';
import {
  lerJanela,
  reabrirJanela,
  useJanelaAtendimento,
  type JanelaAtendimento,
} from '../hooks/useJanelaAtendimento';
import { useGlobalNotification } from '../contexts/GlobalNotificationContext';
import { PERMISSOES, temPermissao } from '../config/permissions';
import { MetaService, type StatusWhatsapp } from '../services/MetaService';
import { ConexaoPlataformaService, PlataformaEnum, StatusConexaoEnum, ehCanalWhatsapp, nomeDoCanal } from '../services/ConexaoPlataformaService';
import {
  LuMessageSquare,
  LuTriangleAlert,
  LuPlug,
  LuPanelRight,
  LuTag,
  LuStickyNote,
  LuCalendarPlus,
  LuPaperclip,
} from 'react-icons/lu';

const CHAVE_FUNDO = 'lemeai_chat_fundo';
const CHAVE_TEXTURA = 'lemeai_chat_textura';

const apiUrl = import.meta.env.VITE_API_URL;

const formatPreviewMessage = (message: string) => {
  if (!message) return '';
  const lowerMsg = message.toLowerCase();

  if (lowerMsg === '[audio]' || lowerMsg.includes('.ogg') || lowerMsg.includes('.mp3') || lowerMsg.includes('.oga') || lowerMsg.includes('.wav') || lowerMsg.includes('.m4a')) {
    return 'Mensagem de voz';
  }
  if (lowerMsg === '[image]' || lowerMsg.includes('.jpg') || lowerMsg.includes('.jpeg') || lowerMsg.includes('.png') || lowerMsg.includes('.webp') || lowerMsg.includes('.gif')) {
    return 'Imagem';
  }
  if (lowerMsg === '[document]' || lowerMsg === '[file]' || lowerMsg.includes('.pdf') || lowerMsg.includes('.doc') || lowerMsg.includes('.docx') || lowerMsg.includes('.xls') || lowerMsg.includes('.xlsx') || lowerMsg.includes('.txt')) {
    return 'Documento';
  }
  return message;
};

/**
 * Carimbo da lista de conversas, no padrão do WhatsApp: hoje mostra a hora,
 * ontem mostra "Ontem", antes disso mostra a data. Sem isso, uma conversa
 * parada há uma semana aparecia como "14:32" e parecia recente.
 *
 * A comparação é por DIA de calendário, não por 24 horas corridas: às 00h30
 * uma mensagem das 23h de ontem é "Ontem", não "hoje". O `Math.round` sobre a
 * diferença entre as meias-noites absorve os dias de 23h e 25h do horário de
 * verão.
 */
const formatarCarimbo = (iso: string) => {
  const data = new Date(iso);
  // `DataUltimaMensagem` volta como DateTime.MinValue quando a conversa não
  // tem mensagem — sem isto a lista mostraria "01/01/0001".
  if (isNaN(data.getTime()) || data.getFullYear() <= 1) return '';

  const meiaNoite = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const dias = Math.round((meiaNoite(new Date()) - meiaNoite(data)) / 86400000);

  if (dias === 0) return data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  if (dias === 1) return 'Ontem';
  return data.toLocaleDateString('pt-BR');
};

/* A ordem da lista: fixadas primeiro, e dentro de cada grupo a conversa mais recente em cima.
   É uma régua só — vale para a carga do servidor e para o que se refaz depois de arquivar ou fixar. */
const compararConversas = (a: { fixada: boolean; quando: number }, b: { fixada: boolean; quando: number }) =>
  Number(b.fixada) - Number(a.fixada) || b.quando - a.quando;

const ordenarConversas = (lista: Contact[]) =>
  [...lista].sort((a, b) =>
    compararConversas(
      { fixada: a.fixada === true, quando: a.ultimaMensagemEm ?? 0 },
      { fixada: b.fixada === true, quando: b.ultimaMensagemEm ?? 0 },
    ));

// Interfaces (sem alteração)
interface CurrentUser {
  id: number;
  nome: string;
}

interface ApiConversation {
  idConversa: number;
  nomeCliente: string;
  numeroWhatsapp: string;
  /** PlataformaEnum da conversa (1/2 WhatsApp, 3 Instagram). */
  plataforma?: number;
  ultimaMensagem: string;
  dataUltimaMensagem: string;
  totalNaoLidas: number;
  idStatus?: number;
  valor?: number;
  nomeResponsavel?: string;
  /** Id do responsavel; nulo com a IA. O filtro por responsavel casa por ele, nao pelo nome. */
  idResponsavel?: number | null;
  /** Quem mandou a ultima mensagem (0 Cliente, 1 Vendedor, 2 IA); nulo em conversa sem mensagem. */
  ultimaMensagemOrigem?: number | null;
  idContato?: number | null;
  foraDoFunil?: boolean;
  foraDoFunilMotivo?: string | null;
  tipoContatoId?: number | null;
  tipoLeadId?: number;
  tipoLeadNome?: string;
  campanha?: boolean;
  idCampanha?: number | null;
  nomeCampanha?: string | null;
  /* Já vinha no payload e era descartado aqui no `.map()` desde que a
     atribuição de anúncio foi para a API. */
  atribuicaoAnuncio?: AtribuicaoAnuncio | null;
  /* A rota traz TUDO, arquivadas incluídas: quem separa caixa de pasta é esta tela. Os três campos são
     datas (nulo = não), e não booleanos, porque a API quer dizer também "desde quando". */
  arquivadaEm?: string | null;
  marcadaNaoLidaEm?: string | null;
  fixadaEm?: string | null;
  /* Silenciar e lembrar depois (fase 3): também datas. Silêncio vencido e lembrete vencido valem como
     "não tem" — a tela resolve na leitura, sem depender do relógio da API. */
  silenciadaAte?: string | null;
  lembrarEm?: string | null;
}

interface ApiMessage {
  idMensagem: number;
  idConversa: number;
  mensagem: string;
  origemMensagem: number; // 0 = Cliente, 1 = Vendedor, 2 = IA
  dataEnvio: string;
  tipoMensagem?: 'text' | 'image' | 'audio' | 'file' | 'document';
  urlMidia?: string;
  caminhoArquivo?: string;
  reacao?: string | null;
  statusMensagem?: string; // "enviada" | "entregue" | "lida"
  apagadaEm?: string | null;
}

interface MessagesByDate {
  [date: string]: Message[];
}

const ChatPage = () => {
  const navigate = useNavigate();
  // Estados da UI (sem alteração)
  /* Três colunas permanentes só cabem a partir de ~1630px (620 de conversa +
     300 de lista + 360 de painel + respiros + 256 da sidebar). Abaixo disso o
     painel de Contato vem recolhido no trilho — senão a conversa nasce
     espremida, que é o defeito que esta tela veio consertar. */
  const [isDetailsPanelOpen, setDetailsPanelOpen] = useState(() => window.innerWidth >= 1630);


  // Estados de dados (sem alteração)
  const [contacts, setContacts] = useState<Contact[]>([]);
  /* Quem tem `conversas.todos` carrega a empresa INTEIRA e recorta na tela. Antes havia um
     seletor "Minhas / Da equipe" que trocava o parâmetro da rota e recarregava tudo: era
     binário (eu × empresa), não combinava com nenhum outro filtro e não respondia "a Ana e o
     Bruno", que é o que ele pediu em 13/10. Agora o recorte por pessoa é filtro de front, e a
     carga é uma só — a mesma que "Da equipe" já fazia.
     A permissão continua valendo para abrir mensagem e transferir conversa de outro vendedor. */
  const verTodas = temPermissao(PERMISSOES.conversasTodos);
  const [selectedContactId, setSelectedContactId] = useState<number | null>(null);
  /* A conversa aberta, lida pela recarga da lista sem entrar nas dependências dela — senão cada
     clique numa conversa refaria a carga inicial. */
  const conversaAberta = useRef<number | null>(null);
  conversaAberta.current = selectedContactId;
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [activeConversationMessages, setActiveConversationMessages] = useState<MessagesByDate>({});
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);

  // Consome o status global do Hub ao invés de gerenciar localmente
  const { isHubConnected, recarregarResumo } = useGlobalNotification();

  // --- WhatsApp Status ---
  const [whatsappStatus, setWhatsappStatus] = useState<'checking' | 'connected' | 'disconnected' | 'no-instance' | null>(null);

  const isWhatsappDisabled = whatsappStatus === 'disconnected' || whatsappStatus === 'no-instance';
  /* Instagram conectado: a empresa tem canal mesmo sem WhatsApp, e a tela não pode virar
     só o convite para conectar WhatsApp. */
  const [temInstagram, setTemInstagram] = useState(false);
  useEffect(() => {
    ConexaoPlataformaService.buscarConexoesAtivas()
      .then(r => setTemInstagram((r?.sucesso ? r.dados ?? [] : [])
        .some(c => c.plataforma === PlataformaEnum.Instagram && c.status === StatusConexaoEnum.Ativa)))
      .catch(() => setTemInstagram(false));
  }, []);
  /* Uso do plano e motivo de bloqueio, para os cartões de conectar. */
  const [statusWa, setStatusWa] = useState<StatusWhatsapp | null>(null);
  /* QR Code criado e nunca pareado (ou que caiu): o cartão reaproveita, em vez de criar outro. */
  const [qrPendente, setQrPendente] = useState<{ id: number; nome?: string } | null>(null);

  // Janela de 24h da Meta da conversa aberta. `null` enquanto carrega: não trava.
  const [janela, setJanela] = useState<JanelaAtendimento | null>(null);
  const janelaFechada = useJanelaAtendimento(janela);

  /* Aparência da conversa: preferência de quem usa, guardada no navegador.
     Não passa pela API — vale para todas as conversas daquela pessoa. */
  const [fundo, setFundo] = useState<string>(() => localStorage.getItem(CHAVE_FUNDO) || 'bege');
  const [textura, setTextura] = useState<boolean>(() => localStorage.getItem(CHAVE_TEXTURA) !== 'off');
  const [secaoFoco, setSecaoFoco] = useState<SecaoContato | undefined>();
  const [nomeAgente, setNomeAgente] = useState<string>('');

  // Resumo da conversa, acionado pelo botão do cabeçalho da conversa.
  const [resumoAberto, setResumoAberto] = useState(false);
  const [resumo, setResumo] = useState('');
  const [gerandoResumo, setGerandoResumo] = useState(false);

  /* A pasta aberta (Arquivadas ou Lembretes) e a conversa cujo "Transferir para…" está na tela. Moram aqui, e não na
     lista: este componente recarrega a lista em várias situações (mensagem nova, envio, ação), e o que
     estivesse dentro do ContactList esvaziaria debaixo de quem estivesse lendo. Não sobrevive ao F5 —
     voltar de um recarregamento dentro de "Arquivadas" pareceria perda de conversas. */
  const [modo, setModo] = useState<ModoLista>('caixa');
  const [transferirId, setTransferirId] = useState<number | null>(null);

  /* "Criar tarefa" do menu da conversa: a conversa-alvo e os tipos de tarefa da empresa. Sem tipo
     cadastrado o item do menu some (não há como criar a tarefa). */
  const [tarefaParaId, setTarefaParaId] = useState<number | null>(null);
  const [tiposTarefa, setTiposTarefa] = useState<TipoTarefa[]>([]);

  /* Atalhos do "/" e do raio no compositor. Vêm da API uma vez; só os ativos chegam ao compositor. */
  const [atalhos, setAtalhos] = useState<Atalho[]>([]);

  const trocarFundo = (novo: string) => {
    setFundo(novo);
    localStorage.setItem(CHAVE_FUNDO, novo);
  };
  const trocarTextura = (ligada: boolean) => {
    setTextura(ligada);
    localStorage.setItem(CHAVE_TEXTURA, ligada ? 'on' : 'off');
  };

  const handleResumir = async (conversaId: number) => {
    if (gerandoResumo) return;
    setGerandoResumo(true);
    const toastId = aviso.carregando('Gerando resumo da conversa com IA…');
    try {
      const resposta = await ChatService.getConversationSummary(conversaId);
      if (resposta.sucesso) {
        setResumo(resposta.dados);
        setResumoAberto(true);
        aviso.ok('Resumo gerado.', { id: toastId });
      } else {
        aviso.erro(resposta.mensagem || 'Não foi possível gerar o resumo.', { id: toastId });
      }
    } catch (err) {
      console.error('Erro ao gerar resumo:', err);
      aviso.erro('Não foi possível conectar com o serviço de IA.', { id: toastId });
    } finally {
      setGerandoResumo(false);
    }
  };

  const abrirSecao = (secao: SecaoContato) => {
    setDetailsPanelOpen(true);
    setSecaoFoco(secao);
  };

  // Estável de propósito: o painel usa isto como dependência de efeito.
  const limparFoco = useCallback(() => setSecaoFoco(undefined), []);


  // Funções de busca de dados (sem alteração na lógica interna, exceto a remoção do setupChat)
  const fetchCurrentUser = useCallback(async () => {
    console.log('Auth Debug - Fetching current user...');
    try {
      const response = await apiFetch(`${apiUrl}/api/Auth/me`);
      console.log('Auth Debug - Response Status:', response.status);

      if (!response.ok) {
        console.warn('Auth Debug - Response not OK');
        return;
      }
      const result = await response.json();

      // API returns the user object directly or a wrapped response
      if (result.sucesso && result.dados) {
        // Wrapped response format
        const userId = result.dados.id || result.dados.userId || 0;
        setCurrentUser({ id: userId, nome: result.dados.userName || result.dados.nome });
      } else if (result.id) {
        // Direct user object format
        const userId = Number(result.id) || 0;
        setCurrentUser({ id: userId, nome: result.userName || result.nome });
      } else {
        // Fallback if structure is unknown or failure
        setCurrentUser({ id: 0, nome: 'Lucas Almeida' });
      }
    } catch (err) {
      console.error("Erro ao buscar usuário logado:", err);
      // Fallback on error - Use a realistic name to demonstrate the feature
      setCurrentUser({ id: 0, nome: 'Lucas Almeida' });
    }
  }, []);

  const fetchConversations = useCallback(async (isInitialLoad = false) => {
    // ...código original sem alteração

    if (isInitialLoad) setIsLoading(true);
    try {
      // Sem `conversas.todos` a API ignora o parâmetro — nem mandamos.
      const response = await apiFetch(
        `${apiUrl}/api/Chat/ConversasPorVendedor${verTodas ? '?incluirTodos=true' : ''}`);

      // 401 check removed

      if (!response.ok) {
        if (response.status === 400 || response.status === 404) {
          setContacts([]);
          return;
        }
      }

      let result;
      try {
        result = await response.json();
      } catch (e) {
        console.warn("Failed to parse chat conversations JSON", e);
        setContacts([]);
        return;
      }
      if (result.sucesso && Array.isArray(result.dados)) {
        const sortedConversations: ApiConversation[] = result.dados.sort((a: ApiConversation, b: ApiConversation) =>
          compararConversas(
            { fixada: !!a.fixadaEm, quando: new Date(a.dataUltimaMensagem).getTime() },
            { fixada: !!b.fixadaEm, quando: new Date(b.dataUltimaMensagem).getTime() },
          )
        );
        const formattedContacts: Contact[] = sortedConversations.map((convo: ApiConversation) => {
          return {
            id: convo.idConversa,
            name: convo.nomeCliente || convo.numeroWhatsapp,
            lastMessage: formatPreviewMessage(convo.ultimaMensagem),
            time: formatarCarimbo(convo.dataUltimaMensagem),
            /* Mensagem que chega na conversa aberta já está sendo lida: o servidor só zera o
               número quando a conversa é carregada, e a lista não acende o que está na tela. */
            unread: convo.idConversa === conversaAberta.current && document.visibilityState === 'visible' ? 0 : convo.totalNaoLidas,
            arquivada: !!convo.arquivadaEm,
            /* Lembrete que já venceu volta marcada como não lida — é o que a API faz ao acordar, e a tela não
               espera o relógio dela. */
            marcadaNaoLida: !!convo.marcadaNaoLidaEm || (!!convo.lembrarEm && new Date(convo.lembrarEm).getTime() <= Date.now()),
            fixada: !!convo.fixadaEm,
            silenciadaAte: convo.silenciadaAte ? new Date(convo.silenciadaAte).getTime() : null,
            lembrarEm: convo.lembrarEm ? new Date(convo.lembrarEm).getTime() : null,
            ultimaMensagemEm: new Date(convo.dataUltimaMensagem).getTime(),
            initials: (convo.nomeCliente || 'C').charAt(0).toUpperCase(),
            phone: convo.numeroWhatsapp,
            plataforma: convo.plataforma,
            // Etapa, valor, responsável e a marca de funil vêm da própria listagem: o Chat não
            // depende mais da lista de oportunidades (que não traz contato fora do funil).
            statusId: convo.idStatus || 1,
            detailsValue: convo.valor || 0,
            responsibleName: convo.nomeResponsavel || 'IA',
            responsibleId: convo.idResponsavel ?? null,
            ultimaMensagemDe: convo.ultimaMensagemOrigem ?? null,
            contactId: convo.idContato ?? null,
            foraDoFunil: convo.foraDoFunil === true,
            foraDoFunilMotivo: convo.foraDoFunilMotivo ?? null,
            tipoContatoId: convo.tipoContatoId ?? null,
            tipoLeadId: convo.tipoLeadId,
            tipoLeadNome: convo.tipoLeadNome,
            origem: convo.campanha ? 'marketing' : 'organic',
            idCampanha: convo.idCampanha ?? null,
            atribuicaoAnuncio: convo.atribuicaoAnuncio ?? null,
            messagesByDate: {}
          };
        });
        setContacts(formattedContacts);
        // Removed auto-selection logic to allow user to choose conversation manually
      } else {
        setContacts([]);
      }
    } catch (err) {
      setError("Ocorreu um erro de rede. Tente novamente.");
    } finally {
      setTimeout(() => setIsLoading(false), 500);
    }
  }, [navigate, verTodas]);

  const mapApiStatus = (statusMensagem?: string): Message['status'] => {
    switch (statusMensagem) {
      case 'entregue':
      case 'delivered': return 'delivered';
      case 'lida':
      case 'read':     return 'read';
      case 'enviada':
      case 'sent':  return 'sent';
      default:         return 'sent';
    }
  };

  const fetchMessages = useCallback(async (contactId: number) => {
    try {
      /* Abrir a conversa zera o `unread_count` dela no servidor — então o número do
         menu tem de ser medido de novo, senão fica aceso dizendo o que já foi lido. */
      const response = await apiFetch(`${apiUrl}/api/Chat/Conversas/${contactId}/Mensagens`);
      if (!response.ok) throw new Error('Falha ao buscar mensagens.');
      const result = await response.json();
      // Suporta tanto result.dados (array direto) quanto result.dados.mensagens (formato legado)
      const rawMessages: ApiMessage[] = Array.isArray(result.dados)
        ? result.dados
        : Array.isArray(result.dados?.mensagens)
          ? result.dados.mensagens
          : [];
      setJanela(lerJanela(result.dados));
      if (result.sucesso && rawMessages.length >= 0) {
        const messagesByDate = rawMessages.reduce((acc: MessagesByDate, msg: ApiMessage) => {
          const date = new Date(msg.dataEnvio).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
          const formattedMessage: Message = {
            id: msg.idMensagem,
            text: msg.mensagem,
            sender: msg.origemMensagem === 0 ? 'other' : (msg.origemMensagem === 1 ? 'me' : 'ia'),
            time: new Date(msg.dataEnvio).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
            status: mapApiStatus(msg.statusMensagem),
            type: msg.tipoMensagem || 'text',
            mediaUrl: msg.urlMidia || msg.caminhoArquivo,
            reacao: msg.reacao ?? null,
            apagadaEm: msg.apagadaEm ?? null,
          };
          if (!acc[date]) acc[date] = [];
          acc[date].push(formattedMessage);
          return acc;
        }, {});
        setActiveConversationMessages(messagesByDate);
      } else {
        setActiveConversationMessages({});
      }
    } catch (err) {
      console.error("Erro ao buscar mensagens:", err);
      setActiveConversationMessages({});
      setJanela(null);
    } finally {
      recarregarResumo();
    }
  }, [navigate, recarregarResumo]);

  // ADIÇÃO 1: Função para processar novas mensagens recebidas via Hub
  const handleNewMessage = useCallback((newMessage: ApiMessage) => {
    console.log("Nova mensagem recebida via Hub (RAW):", newMessage);
    console.log("Campos de mídia:", {
      tipo: newMessage.tipoMensagem,
      url: newMessage.urlMidia,
      caminho: newMessage.caminhoArquivo
    });

    // Atualiza a conversa na tela APENAS se ela for a que está aberta
    if (newMessage.idConversa === selectedContactId) {
      const formattedMessage: Message = {
        id: newMessage.idMensagem,
        text: newMessage.mensagem,
        sender: newMessage.origemMensagem === 0 ? 'other' : (newMessage.origemMensagem === 1 ? 'me' : 'ia'),
        time: new Date(newMessage.dataEnvio).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
        status: mapApiStatus(newMessage.statusMensagem),
        type: newMessage.tipoMensagem || 'text',
        mediaUrl: newMessage.urlMidia || newMessage.caminhoArquivo,
        reacao: newMessage.reacao ?? null,
        apagadaEm: newMessage.apagadaEm ?? null,
      };

      const dateKey = new Date(newMessage.dataEnvio).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });

      // O cliente escreveu: a janela de 24h reabre agora, sem recarregar a conversa.
      if (newMessage.origemMensagem === 0) setJanela(reabrirJanela);

      setActiveConversationMessages(prev => {
        const newMessagesByDate = { ...prev };
        const currentMessages = prev[dateKey] || [];

        // Evita duplicatas se o websocket enviar o mesmo evento
        if (currentMessages.some(m => m.id === formattedMessage.id)) {
          return prev;
        }

        newMessagesByDate[dateKey] = [...currentMessages, formattedMessage];
        return newMessagesByDate;
      });
    }
    /* A lista não é recarregada aqui: o servidor manda também o "ConversaAtualizada" desta
       mesma mensagem, e é ele que sobe a conversa (abaixo). */
  }, [selectedContactId]);

  // ADIÇÃO 2: useEffect para registrar handler de novas mensagens na tela de chat
  useEffect(() => {
    if (isHubConnected) {
      hubService.on('ReceiveNewMessage', handleNewMessage);
    }
    return () => {
      hubService.off('ReceiveNewMessage', handleNewMessage);
    };
  }, [isHubConnected, handleNewMessage]);

  /* Mensagem nova em QUALQUER conversa da empresa (não só na aberta): a lista é medida de novo,
     e a conversa sobe com a prévia e o número de não lidas, como no WhatsApp. O evento não traz o
     texto — quem decide o que cada um vê é a própria rota da lista. A espera junta uma rajada
     (IA respondendo, cliente mandando várias seguidas) numa chamada só. */
  const recargaDaLista = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!isHubConnected) return;
    const aoAtualizar = () => {
      window.clearTimeout(recargaDaLista.current);
      recargaDaLista.current = window.setTimeout(() => fetchConversations(false), 400);
    };
    hubService.on('ConversaAtualizada', aoAtualizar);
    return () => {
      hubService.off('ConversaAtualizada', aoAtualizar);
      window.clearTimeout(recargaDaLista.current);
    };
  }, [isHubConnected, fetchConversations]);

  /* Mensagem apagada para todos no WhatsApp: só marca a que já está na tela. Evento próprio
     porque o ReceiveNewMessage também dispara o aviso de mensagem nova. */
  useEffect(() => {
    if (!isHubConnected) return;
    const aoApagar = (evento: { idConversa: number; idMensagem: number; apagadaEm: string }) => {
      if (evento.idConversa !== selectedContactId) return;
      setActiveConversationMessages(prev => {
        const atualizado: MessagesByDate = {};
        for (const [data, mensagens] of Object.entries(prev)) {
          atualizado[data] = mensagens.map(m => (m.id === evento.idMensagem ? { ...m, apagadaEm: evento.apagadaEm } : m));
        }
        return atualizado;
      });
    };
    hubService.on('MensagemApagada', aoApagar);
    return () => {
      hubService.off('MensagemApagada', aoApagar);
    };
  }, [isHubConnected, selectedContactId]);

  // ADIÇÃO 3 E MUDANÇA: useEffect para gerenciar entrada/saída de grupos e buscar mensagens
  useEffect(() => {
    // Só executa se o Hub estiver conectado E um contato estiver selecionado
    if (isHubConnected && selectedContactId !== null) {
      const currentContactId = selectedContactId;

      console.log(`Tentando entrar no grupo ${currentContactId}...`);
      hubService.invoke('JoinConversationGroup', currentContactId)
        .then(() => console.log(`Entrou no grupo ${currentContactId} com sucesso.`))
        .catch(err => console.error(`Erro ao entrar no grupo ${currentContactId}:`, err));

      // Função de limpeza para sair do grupo
      return () => {
        console.log(`Tentando sair do grupo ${currentContactId}...`);
        hubService.invoke('LeaveConversationGroup', currentContactId)
          .then(() => console.log(`Saiu do grupo ${currentContactId} com sucesso.`))
          .catch(err => console.error(`Erro ao sair do grupo ${currentContactId}:`, err));
      };
    }
  }, [isHubConnected, selectedContactId]);// Executa quando o contato selecionado muda

  // O useEffect que busca as mensagens agora não precisa mais se preocupar com o Hub
  useEffect(() => {
    if (selectedContactId !== null) {
      fetchMessages(selectedContactId);
    }
  }, [selectedContactId, fetchMessages]);





  // useEffect original para a carga inicial (sem alteração)
  useEffect(() => {
    fetchCurrentUser();
    fetchConversations(true);
  }, [fetchCurrentUser, fetchConversations]);

  const checkWhatsappConnection = useCallback(async () => {
    setWhatsappStatus('checking');
    try {
      const r = await MetaService.getWhatsappStatus();
      /* Status que não respondeu não é "sem WhatsApp": sem esta guarda, uma
         falha de rede trocaria o Chat inteiro pela tela de conectar. */
      if (!r.sucesso || !r.dados) {
        setWhatsappStatus('connected');
        return;
      }
      setStatusWa(r.dados);
      setQrPendente(null);
      if (r.dados.usaAPIMeta) { setWhatsappStatus('connected'); return; }
      if (!r.dados.usaAPIEvolution) { setWhatsappStatus('no-instance'); return; }

      /* O status conta QUALQUER conexão de QR Code, inclusive a que foi criada e
         nunca pareou (o painel cria ao abrir). Só vale como conectado se houver
         uma Ativa. A lista vem filtrada por usuário para quem não é admin com
         multi-contas, então só se rebaixa quando ela MOSTRA QR Codes e nenhum
         está ativo — sem QR Code na lista, fica o que o status disse. */
      const lista = await ConexaoPlataformaService.buscarConexoesAtivas().catch(() => null);
      const qrs = (lista?.sucesso ? lista.dados ?? [] : []).filter(c => c.plataforma === PlataformaEnum.WhatsappEvolution);
      if (qrs.length > 0 && !qrs.some(c => c.status === StatusConexaoEnum.Ativa)) {
        setQrPendente({ id: qrs[0].conexaoPlataformaId, nome: qrs[0].nome });
        setWhatsappStatus('no-instance');
      } else {
        setWhatsappStatus('connected');
      }
    } catch (err) {
      console.error("Erro ao verificar conexão:", err);
      setWhatsappStatus('connected');
    }
  }, []);

  useEffect(() => {
    checkWhatsappConnection();
  }, [checkWhatsappConnection]);

  /* Nome do agente para assinar as bolhas da IA ("Téo · IA"). Uma chamada só,
     na carga; sem ela a assinatura cai para "IA", que ainda distingue. */
  useEffect(() => {
    let vivo = true;
    RegrasIAService.getAgentes()
      .then(resp => {
        if (!vivo || !resp?.sucesso || !Array.isArray(resp.dados)) return;
        const padrao = resp.dados.find(a => a.isDefault) || resp.dados[0];
        if (padrao?.nomeAgente) setNomeAgente(padrao.nomeAgente);
      })
      .catch(() => { /* sem nome, a bolha assina só "IA" */ });
    return () => { vivo = false; };
  }, []);

  useEffect(() => {
    let vivo = true;
    TipoTarefaService.getAll()
      .then(t => { if (vivo) setTiposTarefa(t); })
      .catch(() => { /* sem tipos, o item "Criar tarefa" simplesmente não aparece */ });
    AtalhoService.getAll()
      .then(r => { if (vivo && Array.isArray(r.dados)) setAtalhos(r.dados.filter(a => a.ativo)); })
      .catch(() => { /* sem atalhos, o "/" abre vazio e aponta para o cadastro */ });
    return () => { vivo = false; };
  }, []);

  // Lógica de manipulação de eventos e renderização (sem alteração)
  const selectedContact = contacts.find(c => c.id === selectedContactId);

  /* `/chat?conversa=123` abre direto naquela conversa — é como a Pipeline e o Disparador mandam para
     cá. Só na primeira vez que a lista chega: depois disso quem manda é o clique, e o parâmetro sai do
     endereço para um F5 não reabrir sempre a mesma. */
  const [parametros, setParametros] = useSearchParams();
  const conversaPedida = parametros.get('conversa');
  const jaAbriuPedida = useRef(false);
  useEffect(() => {
    if (jaAbriuPedida.current || !conversaPedida || !contacts.length) return;
    const id = Number(conversaPedida);
    jaAbriuPedida.current = true;
    const novos = new URLSearchParams(parametros);
    novos.delete('conversa');
    setParametros(novos, { replace: true });
    if (contacts.some(c => c.id === id)) handleSelectContact(id);
    else aviso.erro('Essa conversa não está na sua caixa de entrada.');
  }, [conversaPedida, contacts]);

  const handleSelectContact = (id: number) => {
    /* Abrir a conversa já é a leitura: o número sai da linha na hora do clique.
       O servidor zera o `unread_count` quando `fetchMessages` carrega a conversa,
       mas a lista só saberia disso na próxima medida — até lá o badge ficaria
       aceso apontando o que a pessoa está lendo. */
    setContacts(anteriores =>
      anteriores.some(c => c.id === id && (c.unread > 0 || c.marcadaNaoLida || c.lembrarEm))
        ? anteriores.map(c => (c.id === id ? { ...c, unread: 0, marcadaNaoLida: false, lembrarEm: null } : c))
        : anteriores
    );

    /* Abrir a conversa limpa a marca manual — o servidor faz isso ao carregar as mensagens, mas clicar na
       que JÁ está aberta não as recarrega: sem esta chamada a marca voltaria na próxima medida da lista. */
    if (id === selectedContactId && contacts.find(c => c.id === id)?.marcadaNaoLida) {
      ChatService.marcarNaoLida(id, false).catch(() => fetchConversations(false));
    }
    /* Abrir a conversa também cancela o lembrete (quem abriu já se lembrou). Idem: a API só faz isso ao
       carregar as mensagens, e clicar na conversa que já está aberta não as recarrega. */
    if (id === selectedContactId && contacts.find(c => c.id === id)?.lembrarEm) {
      ChatService.lembrarConversa(id, null).catch(() => fetchConversations(false));
    }

    if (id !== selectedContactId) {
      setActiveConversationMessages({});
      setJanela(null);
      setSelectedContactId(id);
      setSecaoFoco(undefined);
      // Trocar de conversa não reabre o painel: quem o fechou, fechou.
    }
  };

  const handleSendMessage = async (text: string) => {
    // ...código original sem alteração
    if (!text.trim() || selectedContactId === null) return;

    const tempId = Date.now();
    const today = new Date();
    const dateKey = today.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const optimisticMessage: Message = {
      id: tempId,
      text: text,
      sender: 'me',
      time: today.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
      status: 'sending',
    };
    setActiveConversationMessages(prev => {
      const newMessagesByDate = { ...prev };
      const currentMessages = prev[dateKey] || [];
      newMessagesByDate[dateKey] = [...currentMessages, optimisticMessage];
      return newMessagesByDate;
    });
    try {
      const response = await apiFetch(`${apiUrl}/api/Chat/Conversas/${selectedContactId}/EnviarMensagem`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(text),
      });

      // 401 check removed

      if (!response.ok) {
        // A API diz o motivo (ex.: janela de 24h fechada); "verifique sua conexão" mentia.
        const erro = await response.json().catch(() => null);
        throw new Error(erro?.mensagem || 'Não foi possível enviar a mensagem. Verifique sua conexão.');
      }
      await fetchMessages(selectedContactId);
      await fetchConversations(false);
    } catch (err) {
      console.error("Erro ao enviar mensagem:", err);
      setActiveConversationMessages(prev => {
        const newMessagesByDate = { ...prev };
        const messagesForDate = prev[dateKey] ? [...prev[dateKey]] : [];
        const messageIndex = messagesForDate.findIndex(m => m.id === tempId);
        if (messageIndex !== -1) {
          const updatedMessage = { ...messagesForDate[messageIndex], status: 'failed' as const };
          messagesForDate[messageIndex] = updatedMessage;
          newMessagesByDate[dateKey] = messagesForDate;
        }
        return newMessagesByDate;
      });
      aviso.erro(textoDoErro(err, 'Não foi possível enviar a mensagem. Verifique sua conexão.'));
    }
  };

  const handleSendMedia = async (file: File, type: 'image' | 'audio' | 'file') => {
    if (selectedContactId === null) return;

    const tempId = Date.now();
    const today = new Date();
    const dateKey = today.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });

    // Map internal type to Message type
    let messageType: 'image' | 'audio' | 'file' | 'document' = type;
    if (type === 'file') messageType = 'document';

    const optimisticMessage: Message = {
      id: tempId,
      text: type === 'image' ? '[Imagem]' : (type === 'audio' ? '[Áudio]' : file.name),
      sender: 'me',
      time: today.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
      status: 'sending',
      type: messageType,
      mediaUrl: URL.createObjectURL(file)
    };

    setActiveConversationMessages(prev => {
      const newMessagesByDate = { ...prev };
      const currentMessages = prev[dateKey] || [];
      newMessagesByDate[dateKey] = [...currentMessages, optimisticMessage];
      return newMessagesByDate;
    });

    try {
      await ChatService.enviarMidia(selectedContactId, file, type);
      await fetchMessages(selectedContactId);
      await fetchConversations(false);
    } catch (err: any) {
      console.error("Erro ao enviar mídia:", err);
      setActiveConversationMessages(prev => {
        const newMessagesByDate = { ...prev };
        const messagesForDate = prev[dateKey] ? [...prev[dateKey]] : [];
        const messageIndex = messagesForDate.findIndex(m => m.id === tempId);
        if (messageIndex !== -1) {
          const updatedMessage = { ...messagesForDate[messageIndex], status: 'failed' as const };
          messagesForDate[messageIndex] = updatedMessage;
          newMessagesByDate[dateKey] = messagesForDate;
        }
        return newMessagesByDate;
      });
      aviso.erro(textoDoErro(err, 'Não foi possível enviar a mídia.'));
    }
  };


  /* Tira o contato do funil (ou o leva de volta). Toda conversa do mesmo contato muda junto no
     estado local; depois recarrega a lista, porque a API também move da IA para o atendimento
     humano as conversas abertas ao tirar do funil. */
  const alterarFunilDoContato = async (contactId: number, foraDoFunil: boolean) => {
    await ContactService.definirFunil(contactId, foraDoFunil);
    setContacts(atuais => atuais.map(c => c.contactId === contactId
      ? { ...c, foraDoFunil, foraDoFunilMotivo: foraDoFunil ? 'contato' : null }
      : c));
    fetchConversations(false);
  };

  const handleTransferConversation = async (conversaId: number, targetUser: InternalUser) => {
    if (!currentUser) return;

    try {
      // apiFetch e não fetch: com o access token vencido, o fetch cru levava 401
      // e mandava direto para o login, sem nem tentar renovar a sessão.
      const response = await apiFetch(`${apiUrl}/api/Chat/Conversas/${conversaId}/TranferirConversa`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          IdResponsavelAtual: currentUser.id,
          IdNovoResponsavel: targetUser.id
        })
      });

      if (response.status === 401) {
        navigate('/login');
        return;
      }

      if (!response.ok) {
        let errorMessage = 'Falha ao transferir conversa';
        try {
          const errorData = await response.json();
          if (errorData?.mensagem) {
            errorMessage = errorData.mensagem;
          }
        } catch (e) {
          // Ignora erro de parse caso o body esteja vazio
        }
        throw new Error(errorMessage);
      }

      // Success
      aviso.ok(`Conversa transferida para ${targetUser.name}.`);

      // Update state to remove the transferred conversation immediately
      setContacts(prevContacts => prevContacts.filter(c => c.id !== conversaId));
      if (conversaId === selectedContactId) setSelectedContactId(null);

    } catch (err: any) {
      console.error("Erro ao transferir conversa:", err);
      aviso.erro(textoDoErro(err, 'Não foi possível transferir a conversa.'));
    }
  };

  /* ---- Ações do menu de contexto (botão direito na lista e kebab da conversa) ----
     Todas valem na hora, na tela de quem agiu (atualização otimista), e voltam atrás se a API recusar.
     A lista não tem polling nem tempo real: o efeito no servidor aparece para os outros na próxima medida. */
  const aplicarNaConversa = (id: number, ajuste: Partial<Contact>) =>
    setContacts(atuais => ordenarConversas(atuais.map(c => (c.id === id ? { ...c, ...ajuste } : c))));

  /* Aplica o ajuste e devolve o que o desfaz (só as chaves tocadas, com o valor de antes). */
  const ajustarConversa = (contato: Contact, ajuste: Partial<Contact>) => {
    const antes = Object.fromEntries(
      Object.keys(ajuste).map(chave => [chave, contato[chave as keyof Contact]]),
    ) as Partial<Contact>;
    aplicarNaConversa(contato.id, ajuste);
    return () => aplicarNaConversa(contato.id, antes);
  };

  const alternarNaoLida = async (contato: Contact) => {
    const marcar = !contato.marcadaNaoLida;
    const desfazer = ajustarConversa(contato, { marcadaNaoLida: marcar });
    try {
      await ChatService.marcarNaoLida(contato.id, marcar);
    } catch (err) {
      desfazer();
      aviso.erro(textoDoErro(err, 'Não foi possível marcar a conversa.'));
    }
  };

  const alternarArquivada = async (contato: Contact) => {
    const arquivar = !contato.arquivada;
    // Dentro da pasta não existe fixar: arquivar solta a fixação (a API faz o mesmo).
    // Arquivar também cancela o lembrete: a conversa não mora nas duas pastas (a API faz o mesmo).
    const desfazer = ajustarConversa(contato, arquivar ? { arquivada: true, fixada: false, lembrarEm: null } : { arquivada: false });
    try {
      await ChatService.arquivarConversa(contato.id, arquivar);
      // Saiu da caixa: manter aberta na tela seria incoerente. Volta ao estado vazio.
      if (arquivar && contato.id === selectedContactId) setSelectedContactId(null);
      /* Sem botão de desfazer dentro do aviso (o aviso do sistema não tem ação, decisão de 12/09):
         a volta é abrir a pasta e desarquivar. */
      aviso.ok(arquivar ? 'Conversa arquivada · ela está na pasta Arquivadas' : 'Conversa desarquivada.');
    } catch (err) {
      desfazer();
      aviso.erro(textoDoErro(err, 'Não foi possível arquivar a conversa.'));
    }
  };

  const alternarFixada = async (contato: Contact) => {
    const fixar = !contato.fixada;
    /* O limite de 3 é da API (por responsável): a tela só tem as conversas que carregou e contaria outra
       régua. Se ela recusar, a frase pronta dela sobe no aviso e a fixação volta atrás. */
    const desfazer = ajustarConversa(contato, { fixada: fixar });
    try {
      await ChatService.fixarConversa(contato.id, fixar);
    } catch (err) {
      desfazer();
      aviso.erro(textoDoErro(err, 'Não foi possível fixar a conversa.'));
    }
  };

  const silenciar = async (contato: Contact, prazo: PrazoSilencio | null) => {
    const desfazer = ajustarConversa(contato, { silenciadaAte: prazo ? instanteDoSilencio(prazo) : null });
    try {
      const instante = await ChatService.silenciarConversa(contato.id, prazo);
      // O instante que a API calculou substitui o palpite local.
      if (prazo && instante) aplicarNaConversa(contato.id, { silenciadaAte: instante });
      aviso.ok(
        !prazo ? 'Avisos religados.'
          : prazo === 'sempre' ? 'Avisos silenciados até você religar.'
          : `Avisos silenciados por ${prazo === '8h' ? '8 horas' : '1 dia'}.`,
      );
    } catch (err) {
      desfazer();
      aviso.erro(textoDoErro(err, 'Não foi possível silenciar a conversa.'));
    }
  };

  const lembrar = async (contato: Contact, prazo: PrazoLembrete | null) => {
    const em = prazo ? instanteDoLembrete(prazo) : null;
    // Na pasta Lembretes não existe fixar: guardar solta a fixação (a API faz o mesmo).
    const desfazer = ajustarConversa(contato, prazo ? { lembrarEm: em, fixada: false } : { lembrarEm: null });
    try {
      const instante = await ChatService.lembrarConversa(contato.id, prazo);
      if (prazo) {
        const volta = instante ?? em!;
        if (instante) aplicarNaConversa(contato.id, { lembrarEm: instante });
        // Saiu da caixa: manter aberta na tela seria incoerente, como no arquivar.
        if (contato.id === selectedContactId) setSelectedContactId(null);
        aviso.ok(`Conversa guardada em Lembretes · ${textoDaVolta(volta).replace(/^Volta/, 'volta')}`);
      } else {
        aviso.ok('Lembrete cancelado · a conversa voltou para a caixa.');
      }
    } catch (err) {
      desfazer();
      aviso.erro(textoDoErro(err, 'Não foi possível guardar o lembrete.'));
    }
  };

  const abrirContatoDaConversa = (contato: Contact) => {
    handleSelectContact(contato.id);
    setDetailsPanelOpen(true);
    setSecaoFoco(undefined);
  };

  const copiarTelefone = async (contato: Contact) => {
    try {
      await navigator.clipboard.writeText(contato.phone.replace(/\D/g, ''));
      aviso.ok('Telefone copiado.');
    } catch {
      aviso.erro('Não foi possível copiar o telefone.');
    }
  };

  const alternarFunil = async (contato: Contact) => {
    if (!contato.contactId) return;
    const fora = !contato.foraDoFunil;
    const toastId = aviso.carregando(fora ? 'Tirando do funil…' : 'Levando para o funil…');
    try {
      await alterarFunilDoContato(contato.contactId, fora);
      aviso.ok(fora ? 'Contato fora do funil.' : 'Contato de volta ao funil.', { id: toastId });
    } catch (err) {
      aviso.erro(textoDoErro(err, 'Não foi possível alterar o funil do contato.'), { id: toastId });
    }
  };

  const acoes: AcoesConversa = {
    alternarNaoLida,
    alternarArquivada,
    alternarFixada,
    silenciar,
    lembrar,
    transferir: contato => setTransferirId(contato.id),
    abrirContato: abrirContatoDaConversa,
    resumir: contato => { handleResumir(contato.id); },
    copiarTelefone,
    alternarFunil,
    criarTarefa: contato => setTarefaParaId(contato.id),
    verNaPipeline: contato => navigate('/pipeline/deal/' + contato.id),
    permite: {
      verNaPipeline: getUserPermissions().includes('fluxo_vendas'),
      criarTarefa: tiposTarefa.length > 0,
    },
  };

  /* O que a tela já sabe da conversa aberta, para trocar {{nome}}, {{valor}} etc. na hora de inserir um
     atalho — sem chamada nova. Quem escreve é o usuário logado; a empresa vem do mesmo cadastro da sessão. */
  const resolverAtalhoDaConversa = (atalho: Atalho) => {
    let usuario: { nome?: string; empresaDescricao?: string } = {};
    try { usuario = JSON.parse(localStorage.getItem('user') || '{}') ?? {}; } catch { /* sessão ilegível: sem vendedor */ }
    return resolverAtalho(atalho.corpo, {
      nome: selectedContact?.name,
      vendedor: usuario.nome,
      empresa: usuario.empresaDescricao,
      valor: selectedContact?.detailsValue,
      etapa: selectedContact?.statusId ? nomeEtapa(selectedContact.statusId) : null,
    });
  };

  /* Celular: uma coluna por vez. O corte é o do produto inteiro — ver
     `hooks/useCelular.ts`, que é a mesma régua dos `@media` do CSS. */
  const isMobile = useCelular();

  const handleBackToContacts = () => {
    setSelectedContactId(null);
  };

  /* Caixa e pasta saem da mesma lista. O subtítulo da tela conta SEMPRE a caixa, nunca a pasta, e conta
     conversas (não mensagens) com algo pendente — a marca manual de "não lida" entra. */
  /* Lembrete que já passou da hora vale como "de volta": a conversa reaparece na caixa na próxima medida
     da lista, sem depender do relógio da API (e sem polling, de propósito). */
  const emLembrete = (c: Contact, agora: number) => !c.arquivada && !!c.lembrarEm && c.lembrarEm > agora;
  const caixa = useMemo(() => {
    const agora = Date.now();
    return contacts.filter(c => !c.arquivada && !emLembrete(c, agora));
  }, [contacts]);
  const arquivadas = useMemo(() => contacts.filter(c => c.arquivada), [contacts]);
  /* Na pasta, quem volta primeiro fica em cima. */
  const lembretes = useMemo(() => {
    const agora = Date.now();
    return contacts.filter(c => emLembrete(c, agora)).sort((a, b) => (a.lembrarEm ?? 0) - (b.lembrarEm ?? 0));
  }, [contacts]);
  const totalNaoLidas = caixa.filter(c => c.unread > 0 || c.marcadaNaoLida).length;

  /* Esvaziou a pasta (desarquivou a última, ou o lembrete voltou): ela some e a lista volta sozinha
     para a caixa. */
  useEffect(() => {
    if (isLoading) return;
    if ((modo === 'arquivadas' && arquivadas.length === 0) || (modo === 'lembretes' && lembretes.length === 0)) {
      setModo('caixa');
    }
  }, [modo, isLoading, arquivadas.length, lembretes.length]);
  /* Sem nenhum WhatsApp: a faixa vermelha de antes deu lugar à escolha entre
     API oficial e QR Code (docs/designsystem/propostas/chat-sem-conexao.html). */
  const semWhatsapp = isWhatsappDisabled;
  const semConversas = !isLoading && contacts.length === 0;
  /* A trava do WhatsApp só vale para conversa de WhatsApp: a do Instagram envia pelo Instagram. */
  const envioWhatsappTravado = isWhatsappDisabled && ehCanalWhatsapp(selectedContact?.plataforma);
  /* O aviso de WhatsApp vale quando ele caiu, ou quando é o único canal possível. Empresa que
     atende só pelo Instagram e nunca conectou WhatsApp não perdeu nada. */
  const avisarSemWhatsapp = semWhatsapp && (whatsappStatus === 'disconnected' || !temInstagram);
  const apoio = isLoading
    ? 'Carregando conversas…'
    : semWhatsapp && semConversas && !temInstagram
      ? 'Nenhum WhatsApp conectado'
      : `${caixa.length} ${caixa.length === 1 ? 'conversa' : 'conversas'}` +
        (totalNaoLidas > 0 ? ` · ${totalNaoLidas} não ${totalNaoLidas === 1 ? 'lida' : 'lidas'}` : '') +
        (avisarSemWhatsapp ? ' · WhatsApp não conectado' : '');

  const aoConectarWhatsapp = () => {
    checkWhatsappConnection();
    fetchConversations(false);
  };
  const usoWa = {
    usado: statusWa?.whatsappsConectados ?? 0,
    limite: statusWa?.limiteWhatsapp ?? statusWa?.limiteConexaoPorPlataforma ?? null,
  };
  const bloqueioWa = statusWa?.podeConectarWhatsapp === false
    ? statusWa.motivoBloqueioWhatsapp || 'Limite do plano atingido'
    : null;
  const escolha = (modo: 'tela' | 'area', onVoltar?: () => void) => (
    <ChatSemConexao modo={modo} uso={usoWa} bloqueio={bloqueioWa} qrPendente={qrPendente}
      onConectado={aoConectarWhatsapp} onVoltar={onVoltar} />
  );

  const lista = (
    <ContactList
      contacts={caixa}
      arquivadas={arquivadas}
      lembretes={lembretes}
      modo={modo}
      onAbrirPasta={setModo}
      onFecharPasta={() => setModo('caixa')}
      acoes={acoes}
      activeContactId={selectedContactId || 0}
      onSelectContact={handleSelectContact}
      whatsappStatus={whatsappStatus}
      fundo={fundo}
      onFundoChange={trocarFundo}
      textura={textura}
      onTexturaChange={trocarTextura}
      usuarioId={currentUser?.id ?? null}
      podeVerDeOutros={verTodas}
    />
  );

  const conversa = selectedContact ? (
    <div className="ch-conv">
      <ConversationHeader
        contactName={selectedContact.name}
        contactPhone={ehCanalWhatsapp(selectedContact.plataforma) ? selectedContact.phone : nomeDoCanal(selectedContact.plataforma)}
        onToggleDetails={() => {
          setDetailsPanelOpen(!isDetailsPanelOpen);
          setSecaoFoco(undefined);
        }}
        detailsOpen={isDetailsPanelOpen}
        onResumir={() => { handleResumir(selectedContact.id); }}
        contato={selectedContact}
        acoes={acoes}
        conversationId={selectedContact.id}
        onBack={isMobile ? handleBackToContacts : undefined}
        tipoLeadId={selectedContact.tipoLeadId}
        tipoLeadNome={selectedContact.tipoLeadNome}
        onLeadTypeChange={() => fetchConversations(false)}
        foraDoFunil={selectedContact.foraDoFunil}
      />
      {/* O anúncio que trouxe a conversa aparece citado na primeira mensagem do cliente,
          dentro da conversa, como no WhatsApp — não mais numa faixa no topo (17/10/2026).
          O dado vem no mesmo payload da listagem, sem chamada nova. */}
      <ConversationWindow
        messagesByDate={activeConversationMessages}
        conversationId={selectedContact.id}
        atribuicaoAnuncio={selectedContact.atribuicaoAnuncio}
        nomeAgente={nomeAgente}
        fundo={fundo}
        textura={textura}
      />
      <MessageInput
        onSendMessage={handleSendMessage}
        onSendMedia={handleSendMedia}
        disabled={envioWhatsappTravado || janelaFechada !== null}
        disabledMessage={
          envioWhatsappTravado
            ? 'WhatsApp desconectado. Reconecte para enviar mensagens.'
            : janelaFechada ?? undefined
        }
        destinatario={selectedContact.name}
        // No celular o foco abriria o teclado por cima da conversa.
        focoKey={isMobile ? undefined : selectedContact.id}
        atalhos={atalhos}
        resolverAtalho={resolverAtalhoDaConversa}
        aoUsarAtalho={atalho => AtalhoService.registrarUso(atalho.atalhoId, selectedContact.id)}
        aoGerenciarAtalhos={() => navigate('/atalhos')}
      />
    </div>
  ) : semWhatsapp && !temInstagram ? (
    escolha('area')
  ) : (
    <div className="ch-vazio-tela">
      <LuMessageSquare size={28} strokeWidth={1.6} color="var(--ch-faint)" />
      <p className="ch-vazio-t">Escolha uma conversa para atender</p>
      <p className="ch-vazio-s">
        {contacts.length > 0
          ? 'Clique em uma conversa da lista ao lado para ver o histórico e responder.'
          : 'Você ainda não tem conversas ativas.'}
      </p>
    </div>
  );

  /* Painel de Contato recolhido: trilho com uma porta por seção. Cada ícone
     abre o painel já na seção — porta explícita, não "clicar na borda".
     Além dos ícones, o trilho INTEIRO abre o painel: é `div` com
     `role="button"`, nunca `<button>`, porque botão dentro de botão é HTML
     inválido e o navegador desmonta a árvore em silêncio. Os ícones param a
     propagação para não disputar com o clique da faixa. */
  const abrirDoTrilho = (secao: SecaoContato) => (e: React.MouseEvent) => {
    e.stopPropagation();
    abrirSecao(secao);
  };

  const trilho = (
    <div
      className="ch-trilho"
      role="button"
      tabIndex={0}
      title="Abrir Contato"
      aria-label="Abrir painel de Contato"
      onClick={() => setDetailsPanelOpen(true)}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setDetailsPanelOpen(true);
        }
      }}
    >
      <button
        type="button"
        className="is-active"
        onClick={e => {
          e.stopPropagation();
          setDetailsPanelOpen(true);
        }}
        title="Abrir Contato"
      >
        <LuPanelRight size={16} />
      </button>
      <button type="button" onClick={abrirDoTrilho('negociacao')} title="Negociação">
        <LuTag size={16} />
      </button>
      <button type="button" onClick={abrirDoTrilho('anotacoes')} title="Anotações">
        <LuStickyNote size={16} />
      </button>
      <button type="button" onClick={abrirDoTrilho('agenda')} title="Agenda">
        <LuCalendarPlus size={16} />
      </button>
      <button type="button" onClick={abrirDoTrilho('anexos')} title="Anexos">
        <LuPaperclip size={16} />
      </button>
    </div>
  );

  const painel = selectedContact ? (
    <DetailsPanel
      contact={selectedContact}
      onClose={() => {
        setDetailsPanelOpen(false);
        setSecaoFoco(undefined);
      }}
      onUpdate={() => fetchConversations(false)}
      secaoFoco={secaoFoco}
      onFocoAtendido={limparFoco}
      onFunilChange={alterarFunilDoContato}
    />
  ) : null;

  const renderContent = () => {
    if (error) {
      return (
        <div className="ch-vazio-tela">
          <LuTriangleAlert size={26} strokeWidth={1.6} color="var(--ch-faint)" />
          <p className="ch-vazio-t">Não foi possível carregar as conversas</p>
          <p className="ch-vazio-s">{error}</p>
        </div>
      );
    }

    // Sem WhatsApp e sem nenhuma conversa: a lista não teria o que mostrar.
    if (semWhatsapp && semConversas && !temInstagram) return escolha('tela');

    /* Conexão caída com conversas atrás: faixa de alerta no topo do corpo,
       cheia e em tom de perigo, com a porta para a tela de Conexões. É o aviso
       que não dá para ignorar — fica acima de `.ch-cols` (`flex: none`), então
       vale para desktop e celular sem mexer no layout das colunas. */
    const faixaCaiu = avisarSemWhatsapp && (
      <div className="ch-down" role="alert">
        <LuTriangleAlert size={16} />
        <span><b>WhatsApp não conectado.</b> Você não recebe nem envia mensagens até reconectar.</span>
        <button type="button" className="ch-down-acao" onClick={() => navigate('/connections')}>
          <LuPlug size={14} /> Ir para Conexões
        </button>
      </div>
    );

    if (isMobile) {
      /* A faixa fica fora de `.ch-cols`, que no celular continua sendo linha. */
      return (
        <>
          {!selectedContactId && faixaCaiu}
          <div className="ch-cols">
            {!selectedContactId && lista}
            {selectedContactId && conversa}
            {selectedContactId && isDetailsPanelOpen && painel}
          </div>
        </>
      );
    }

    return (
      <>
        {faixaCaiu}
        <div className="ch-cols">
          {lista}
          {conversa}
          {selectedContact && (isDetailsPanelOpen ? painel : trilho)}
        </div>
      </>
    );
  };

  return (
    <div className={`page-shell ch-screen${isMobile && selectedContactId ? ' is-conversa' : ''}`}>
      <PageHeader title="Chat" subtitle={apoio} />
      <div className="page-body ch-fill">
        {renderContent()}
      </div>

      <SummaryModal isOpen={resumoAberto} onClose={() => setResumoAberto(false)} summary={resumo} />

      {tarefaParaId !== null && (() => {
        const alvo = contacts.find(c => c.id === tarefaParaId);
        return alvo ? (
          <CriarTarefaDialog
            conversaId={alvo.id}
            nomeContato={alvo.name}
            tipos={tiposTarefa}
            onFechar={() => setTarefaParaId(null)}
          />
        ) : null;
      })()}

      {transferirId !== null && (
        <TransferModal
          onClose={() => setTransferirId(null)}
          onTransfer={usuario => {
            const conversaId = transferirId;
            setTransferirId(null);
            handleTransferConversation(conversaId, usuario);
          }}
          currentUserId={currentUser?.id}
        />
      )}
    </div>
  );
};

export default ChatPage;
